import {
  Layer,
  LayerSpecification,
  CustomLayerInterface,
  GeoJSONSourceSpecification,
  Map as MapboxMap,
} from 'mapbox-gl';
import LivingMap from '../map/livingmap';
import { GeoJSONSourceProxy } from './geojson-source-proxy';

/**
 * The LayerDelegate controls and manages all _programmatically_ added "layers" & data sources related to the map instance.
 * NOTE that any layer or source added via the stylesheet will be ignored.
 *
 * The main reason for this class is to be able to track different layers. when appending a layer into the mapbox ecosystem, it gets tracked ONLY within the stylesheet.
 * if we'd ever want to switch to a different stylesheet we would loose all this infromation.
 * This class maintains this layer state regardless of whatever stylesheet might be selected (by the user for example)
 *
 * @class LayerDelegate
 */

export class LayerDelegate {
  private sourceProxies: Map<string, GeoJSONSourceProxy> = new Map<
    string,
    GeoJSONSourceProxy
  >();
  private layers: Map<string, Layer | CustomLayerInterface> = new Map<
    string,
    Layer
  >();
  private LMMap: LivingMap;

  constructor(LMMap: LivingMap) {
    this.LMMap = LMMap;
  }

  /**
   * Adds a mapbox layer to this delegate instance.
   * Note: tracking the same layer in different instances of a layer-delegate is a bad idea and should be avoided.
   * @param  {LayerSpecification | CustomLayerInterface} layer
   * @param  {string} before? usefull if a layer is required to be injected before another layer in the layer z index.
   * @returns void
   */
  public addLayer(
    layer: LayerSpecification | CustomLayerInterface,
    before?: string
  ): void {
    let layerWithMetadata: LayerSpecification | CustomLayerInterface;

    if (layer.type !== 'custom') {
      layerWithMetadata = {
        ...layer,
        metadata: {
          programmatic: true,
        },
      };
    } else {
      layerWithMetadata = {
        ...layer,
      };
    }

    const mapInstance = this.LMMap.getMapboxMap();
    mapInstance.addLayer(layerWithMetadata, before);
    this.layers.set(layer.id, layerWithMetadata);
  }

  //TODO: add unit test
  /**
   * If it is required to add/ mutate properties on an already existing layer, use this method.
   * Certainly usefully if the layer definition is in the stylesheet for example.
   * @param id The layer Id to be tracked.
   * @returns {void}
   */
  public trackLayer(id: string): void {
    try {
      const mapInstance = this.LMMap.getMapboxMap();
      const layer = mapInstance.getLayer(id);
      if (!layer) throw new Error('Layer not found');
      this.layers.set(layer.id, layer);
    } catch (err) {
      console.error(`Layer doesnt exist ${id}`);
    }
  }

  /**
   * retrieve the underlying layer instance, if available.
   * @param  {string} id
   * @returns mapboxgl
   */
  public getLayer(id: string): Layer | CustomLayerInterface | undefined {
    return this.layers.get(id);
  }

  /**
   * stop tracking the layer and also remove it from the map.
   * @param  {string} id
   * @returns void
   */
  public removeLayer(id: string): void {
    const mapInstance = this.LMMap.getMapboxMap();
    if (!mapInstance.getLayer(id)) return;
    mapInstance.removeLayer(id);
    this.layers.delete(id);
  }

  /**
   * sets a layout property on the layer. for detailed syntax definition and all the supported properties, please see [here](https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-property)
   * @param  {string} id
   * @param  {string} name
   * @param  {any} value
   * @returns void
   */
  public setLayoutProperty(id: string, name: string, value: unknown): void {
    if (this.layers.get(id)) {
      const mapInstance = this.LMMap.getMapboxMap();

      mapInstance.setLayoutProperty(id, name, value);
      const layer = mapInstance.getLayer(id);
      if (!layer) throw new Error('Layer not found');
      this.layers.set(layer.id, layer);
    } else {
      throw new Error(
        'Layer Delegate not in control of this layer, please use appropriate delegate'
      );
    }
  }

  /**
   * sets multiple layout properties for a single layer
   * @param id Layer ID
   * @param layerLayout Object of Symbol-Layout values (See mapbox docs)
   */
  public setLayoutProperties(
    id: string,
    layerLayout: { [k: string]: any }
  ): void {
    Object.keys(layerLayout).forEach(sourceKey => {
      const layoutObject = layerLayout;
      this.setLayoutProperty(id, sourceKey, layoutObject[sourceKey]);
    });
  }

  /**
   * sets a paint property on the layer. for detailed syntax definition and all the supported properties, please see [here](https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-property)
   *
   * @param  {string} id
   * @param  {string} name
   * @param  {unknown} value
   * @param  {string|undefined} klass?
   * @throws when the layer is not tracked by this delegate it will throw an error.
   * @returns void
   */
  public setPaintProperty(
    id: string,
    name: string,
    value: unknown,
    klass?: Parameters<MapboxMap['setPaintProperty']>[3]
  ): void {
    if (this.layers.get(id)) {
      const mapInstance = this.LMMap.getMapboxMap();
      mapInstance.setPaintProperty(id, name, value, klass);
      const layer = mapInstance.getLayer(id);
      if (!layer) throw new Error('Layer not found');
      this.layers.set(layer.id, layer);
    } else {
      throw new Error(
        'Layer Delegate not in control of this layer, please use appropriate delegate'
      );
    }
  }

  /**
   * Add a GeoJSON source to the map. the id is used to identify this source again.
   * @param  {string} id
   * @param  {GeoJSONSourceSpecification} GeoJSONSourceSpecification?
   * @returns void
   */
  public addSource(
    id: string,
    GeoJSONSource?: GeoJSONSourceSpecification
  ): void {
    const mapInstance = this.LMMap.getMapboxMap();

    const proxy = GeoJSONSource
      ? new GeoJSONSourceProxy(id, GeoJSONSource, this.LMMap)
      : new GeoJSONSourceProxy(
          id,
          mapInstance.getSource(id) as GeoJSONSourceSpecification,
          this.LMMap
        );

    if (GeoJSONSource) mapInstance.addSource(id, GeoJSONSource);
    this.sourceProxies.set(id, proxy);
  }

  /**
   * Given a sourceID, this will remove the source from the map, and will stop tracking it.
   * @param  {string} id
   * @returns void
   */
  public removeSource(id: string): void {
    const mapInstance = this.LMMap.getMapboxMap();

    if (!mapInstance.getSource(id)) return;
    mapInstance.removeSource(id);
    this.sourceProxies.delete(id);
  }

  /**
   * Given an existing source ID, it will return the proxy representation of it.
   * Use this if you want to mutate the source. e.g. add or remove data.
   * @param  {string} id
   * @returns {GeoJSONSourceProxy | undefined}
   */
  public getSourceProxy(id: string): GeoJSONSourceProxy | undefined {
    return this.sourceProxies.get(id);
  }

  /**
   * A re-apply will loop over all tracked layers & sources and will attempt to re-add them to the mapping instance.
   * This should only ever be called when the map is destroyed, or a new stylesheet has been activated.
   * Calling this when the layers are still active will result in weird side effects and/or duplication of layers.
   * @returns void
   */
  public reapply(): void {
    const mapInstance = this.LMMap.getMapboxMap();

    this.removeLayersFromMap();
    this.sourceProxies.forEach((source, key) =>
      mapInstance.addSource(key, source.getGeojson())
    );
    this.layers.forEach(layer => mapInstance.addLayer(layer));
  }

  private removeLayersFromMap(): void {
    const mapInstance = this.LMMap.getMapboxMap();

    this.layers.forEach((_, key) => {
      if (!mapInstance.getLayer(key)) return;
      mapInstance.removeLayer(key);
    });

    this.sourceProxies.forEach((_, key) => {
      if (!mapInstance.getSource(key)) return;
      mapInstance.removeSource(key);
    });
  }
}
